/**
 * Conversación con la Guía del portal (EF `ia-guia-portal`), para el agente que se le pase.
 *
 * JSON sin streaming, así que basta `supabase.functions.invoke`. La conversación vive en estado
 * de React y se copia a `sessionStorage` para no perderla al cambiar de pantalla dentro del
 * portal (el layout no se desmonta, pero un refresh sí). Todo acceso al storage va en try/catch:
 * en una ventana privada o con el storage bloqueado la guía funciona igual, solo no recuerda.
 *
 * La lógica pura (elegir agente, parseo de errores, rutas, armado de estado) está en
 * `lib/guiaPortal`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  GUIA_FUNCION,
  agenteParaRuta,
  clavesGuia,
  normalizarAgentesDisponibles,
  conBloqueo,
  conMensajeUsuario,
  conRespuesta,
  estaBloqueado,
  estadoDesdeConversacion,
  estadoInicial,
  leerErrorGuia,
  normalizarRespuesta,
  parsearCuerpoError,
  prepararMensaje,
  restaurarEstado,
  sanearSugerencias,
  serializarEstado,
  sinUltimoMensajeUsuario,
  type AgenteDisponible,
  type EstadoGuia,
  type GuiaError,
  type MensajeGuia,
} from "@/lib/guiaPortal";
import { claveHistorialChatIa } from "@/hooks/useHistorialChatIa";

const leer = (clave: string): string | null => {
  try {
    return window.sessionStorage.getItem(clave);
  } catch {
    return null;
  }
};

const escribir = (clave: string, valor: string | null) => {
  try {
    if (valor === null) window.sessionStorage.removeItem(clave);
    else window.sessionStorage.setItem(clave, valor);
  } catch {
    // Storage no disponible: la conversación solo vive en memoria.
  }
};

let contadorIds = 0;
const nuevoId = (prefijo: string) => `${prefijo}-${Date.now().toString(36)}-${++contadorIds}`;

/**
 * Agente de IA del `tipo` pedido para la pantalla actual, según `ia_agentes_disponibles()` (ya
 * filtrada por rol). `null` = no hay agente de ese tipo para esta ruta o el usuario no tiene
 * acceso. Si la RPC no existe todavía (migración pendiente) o falla, también `null`: el widget
 * simplemente no aparece. Una sola consulta compartida (misma queryKey) para todos los tipos.
 */
export function useAgenteIa(pathname: string, tipo: string): AgenteDisponible | null {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["ia-agentes-disponibles", user?.id ?? null],
    enabled: !!user,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data: filas, error } = await (supabase as any).rpc("ia_agentes_disponibles");
      if (error) return [];
      return normalizarAgentesDisponibles(filas);
    },
  });
  return agenteParaRuta(data ?? [], pathname, tipo);
}

/** Agente guía de la pantalla actual (ver `useAgenteIa`). */
export const useAgenteGuia = (pathname: string): AgenteDisponible | null => useAgenteIa(pathname, "guia");

export function useGuiaPortal(agente: string, { sugerenciasHabilitadas }: { sugerenciasHabilitadas: boolean }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const sufijo = user?.id ?? "anon";
  const { estado: claveEstado, oculta: claveOculta } = clavesGuia(sufijo, agente);

  const [estado, setEstado] = useState<EstadoGuia>(() => restaurarEstado(leer(claveEstado)));
  const [oculta, setOculta] = useState<boolean>(() => leer(claveOculta) === "1");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<GuiaError | null>(null);
  const [ahora, setAhora] = useState(() => Date.now());

  // Si cambia el usuario de la pestaña, se carga lo suyo (nunca lo del anterior).
  const claveCargada = useRef(claveEstado);
  useEffect(() => {
    if (claveCargada.current === claveEstado) return;
    claveCargada.current = claveEstado;
    setEstado(restaurarEstado(leer(claveEstado)));
    setOculta(leer(claveOculta) === "1");
    setError(null);
  }, [claveEstado, claveOculta]);

  useEffect(() => {
    escribir(claveEstado, estado.mensajes.length || estado.bloqueadoHasta ? serializarEstado(estado) : null);
  }, [claveEstado, estado]);

  // Mientras dure el bloqueo por límite, el reloj avanza para actualizar los minutos y
  // rehabilitar el input solo.
  const bloqueado = estaBloqueado(estado, ahora);
  useEffect(() => {
    if (estado.bloqueadoHasta === null) return;
    const t = window.setInterval(() => setAhora(Date.now()), 15_000);
    return () => window.clearInterval(t);
  }, [estado.bloqueadoHasta]);
  useEffect(() => {
    if (!bloqueado && error?.codigo === "limite_alcanzado" && estado.bloqueadoHasta !== null) setError(null);
  }, [bloqueado, error, estado.bloqueadoHasta]);

  /** Turno vigente: "Nueva conversación" a medio vuelo descarta la respuesta que llegue tarde. */
  const generacion = useRef(0);
  const ultimoTexto = useRef<string | null>(null);
  const estadoRef = useRef(estado);
  estadoRef.current = estado;

  const ocultar = useCallback(() => {
    setOculta(true);
    escribir(claveOculta, "1");
  }, [claveOculta]);

  /**
   * Llama a la función. Devuelve el texto que conviene regresar al input cuando la pregunta
   * no se procesó (límite, mensaje inválido, deshabilitado), o `null`.
   * `nueva`: la pregunta abre conversación propia (repetir un informe con datos de hoy sin
   * arrastrar el contexto de la anterior, que se queda intacta en el historial).
   */
  const pedir = useCallback(
    async (texto: string, rutaActual: string, agregar: boolean, nueva = false): Promise<string | null> => {
      const gen = ++generacion.current;
      const vigente = () => generacion.current === gen;
      ultimoTexto.current = texto;
      setError(null);
      setEnviando(true);
      const idConversacion = nueva ? null : estadoRef.current.idConversacion;
      if (nueva) {
        const limpio = (e: EstadoGuia): EstadoGuia => ({
          ...estadoInicial(),
          restantesHora: e.restantesHora,
          bloqueadoHasta: e.bloqueadoHasta,
        });
        estadoRef.current = limpio(estadoRef.current);
        setEstado((e) => conMensajeUsuario(limpio(e), texto, nuevoId("u")));
      } else if (agregar) {
        setEstado((e) => conMensajeUsuario(e, texto, nuevoId("u")));
      }

      try {
        const { data, error: errInvoke } = await supabase.functions.invoke(GUIA_FUNCION, {
          body: {
            agente,
            mensaje: texto,
            id_conversacion: idConversacion,
            ruta_actual: rutaActual,
          },
        });
        if (!vigente()) return null;

        const resp = errInvoke ? null : normalizarRespuesta(data);
        if (resp) {
          ultimoTexto.current = null;
          setEstado((e) => conRespuesta(e, resp, nuevoId("a")));
          // Conversación nueva o fecha del último mensaje movida: la lista del historial cambia.
          void qc.invalidateQueries({ queryKey: claveHistorialChatIa(sufijo, agente) });
          return null;
        }

        // 200 con `{ error }` en el cuerpo o fuera de contrato: se trata igual que un error HTTP.
        const err = errInvoke ? await leerErrorGuia(errInvoke) : parsearCuerpoError(data);
        if (!vigente()) return null;

        switch (err.codigo) {
          case "sin_permiso":
            ocultar();
            setEstado((e) => sinUltimoMensajeUsuario(e));
            return null;
          case "limite_alcanzado": {
            const t = Date.now();
            setAhora(t);
            setEstado((e) => conBloqueo(sinUltimoMensajeUsuario(e), err.reintentarEnS, t));
            setError(err);
            return texto;
          }
          case "mensaje_invalido":
          case "deshabilitado":
            setEstado((e) => sinUltimoMensajeUsuario(e));
            setError(err);
            return texto;
          default:
            // error_modelo: la pregunta se queda en el hilo y se ofrece "Reintentar".
            setError(err);
            return null;
        }
      } catch {
        if (vigente()) setError(parsearCuerpoError(null));
        return null;
      } finally {
        if (vigente()) setEnviando(false);
      }
    },
    [agente, ocultar, qc, sufijo],
  );

  const enviar = useCallback(
    async (texto: string, rutaActual: string, { nueva = false }: { nueva?: boolean } = {}): Promise<string | null> => {
      const mensaje = prepararMensaje(texto);
      if (!mensaje || enviando || estaBloqueado(estadoRef.current, Date.now())) return null;
      return pedir(mensaje, rutaActual, true, nueva);
    },
    [enviando, pedir],
  );

  const reintentar = useCallback(
    async (rutaActual: string) => {
      const texto = ultimoTexto.current;
      if (!texto || enviando) return;
      await pedir(texto, rutaActual, false);
    },
    [enviando, pedir],
  );

  const nuevaConversacion = useCallback(() => {
    generacion.current++;
    ultimoTexto.current = null;
    setEnviando(false);
    setError(null);
    // El bloqueo por límite es de la hora, no de la conversación: se conserva.
    setEstado((e) => ({ ...estadoInicial(), restantesHora: e.restantesHora, bloqueadoHasta: e.bloqueadoHasta }));
  }, []);

  /** Pone en el panel una conversación del historial; la siguiente pregunta la continúa. */
  const abrirConversacion = useCallback((numero: number, mensajes: MensajeGuia[]) => {
    generacion.current++;
    ultimoTexto.current = null;
    setEnviando(false);
    setError(null);
    setEstado((e) => estadoDesdeConversacion(e, numero, mensajes));
  }, []);

  const sugerencias = useQuery({
    queryKey: ["guia-portal-sugerencias", sufijo, agente],
    enabled: sugerenciasHabilitadas && !oculta,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const { data, error: errInvoke } = await supabase.functions.invoke(GUIA_FUNCION, {
        body: { agente, accion: "sugerencias" },
      });
      if (errInvoke) {
        const err = await leerErrorGuia(errInvoke);
        if (err.codigo === "sin_permiso") ocultar();
        return sanearSugerencias(null);
      }
      return sanearSugerencias(data);
    },
  });

  return {
    mensajes: estado.mensajes,
    idConversacion: estado.idConversacion,
    restantesHora: estado.restantesHora,
    bloqueadoHasta: estado.bloqueadoHasta,
    bloqueado,
    ahora,
    enviando,
    error,
    oculta,
    sugerencias: sugerencias.data ?? null,
    cargandoSugerencias: sugerencias.isLoading,
    enviar,
    reintentar,
    nuevaConversacion,
    abrirConversacion,
  };
}
